import {
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    signal,
    SimpleChanges,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PlayerDialogComponent } from 'src/app/_dialogs/player-dialog/player-dialog.component';
import { ToastrService } from 'ngx-toastr';
import { GolfPlayer, Player, ScheduledEntry } from 'src/app/_interfaces';
import { GolfTournament, MatchFilter } from 'src/app/_interfaces/golf/golf-tournament';
import { AppStringsService, FollowedPlayerService } from 'src/app/_services';
import { formatLocalShortTimeFromCET } from 'src/app/_functions/datetimes';
import { ScheduledEntryStateService } from 'src/app/tournament-section/tournament/scheduled-entry-state.service';
import { filter, Subscription } from 'rxjs';
import { SponsorBannerComponent } from '../../sponsor-banner/sponsor-banner.component';
import { GolfTeamScoreCardComponent } from '../golf-team-score-card/golf-team-score-card.component';
import { GolfMatchFilterComponent } from '../golf-match-filter/golf-match-filter.component';
import { GolfMatchCardsComponent } from '../golf-match-cards/golf-match-cards.component';

import { AppStringComponent } from '../../app-string/app-string.component';
import { ImageComponent } from '../../image/image.component';
import { GolfScorecardComponent } from '../../golf-scorecard/golf-scorecard.component';

export interface RoundScore {
    strokes: number | null;
    thru: string | null;
}

export interface LeaderRow {
    pos: string;
    placeNum: number | null;
    playerName: string;
    fullName: string;
    photo?: string | null;
    player: GolfPlayer | Player;
    tot: string;
    thru: string;
    roundVals: (number | string)[];
    teeTime: string | null;
    startingHole: number | null;
    pairId?: string | number | null;
    sortOrder?: number | null;
    wonViaPlayoff?: boolean;
}

@Component({
    selector: 'app-golf-leaderboard',
    templateUrl: './golf-leaderboard.component.html',
    styleUrls: ['./golf-leaderboard.component.scss'],
    imports: [SponsorBannerComponent, GolfTeamScoreCardComponent, GolfMatchFilterComponent, GolfMatchCardsComponent, AppStringComponent, ImageComponent, GolfScorecardComponent]
})
export class GolfLeaderboardComponent implements OnInit, OnDestroy {
    // @Input() entry!: ScheduledEntry;
    // @Input() golfTour: GolfTournament | null = null;
    golfTour = signal<GolfTournament | null>(null)

    rows: LeaderRow[] = [];
    headerRounds: number[] = [];
    winnerRow: LeaderRow | null = null;
    golf_player: GolfPlayer | Player = {} as GolfPlayer;

    isEmpty = false;
    selectedFilter: MatchFilter | null = null;
    strokeEventStatus: 'cancelled' | 'interrupted' | null = null;

    private readonly EVEN_PAR_KEY = 'golf.par.even';
    private readonly CUT_KEY = 'golf.player.cut';
    private readonly WD_KEY = 'golf.player.wd';
    private readonly DQ_KEY = 'golf.player.dq';
    private sub?: Subscription;

    constructor(
        public appStringsService: AppStringsService,
        private dialog: MatDialog,
        public followedPlayerService: FollowedPlayerService,
        public toast: ToastrService,
        private entryState: ScheduledEntryStateService
    ) { }

    ngOnInit(): void {
        this.sub = this.entryState.tournament
            .pipe(filter((t): t is GolfTournament => !!t?.id))
            .subscribe((t) => {
                this.golfTour.set(t);
                this.buildLeaderboard();
            });
    }

    ngOnDestroy(): void {
        this.sub?.unsubscribe();
    }

    isWinner(row: LeaderRow): boolean {
        return !!this.winnerRow && row.player?.id === this.winnerRow.player?.id;
    }

    isFollowed(player: Player | GolfPlayer): boolean {
        return this.followedPlayerService.isFollowed(player?.id);
    }

    toggleFollow(player: Player | GolfPlayer): void {
        this.followedPlayerService.toggleFollow(player?.id);
    }

    private str(key: string): string {
        return this.appStringsService.cache.get(key) ?? key;
    }

    private getRound(sc: any, idx: number): RoundScore {
        const raw = sc[`round_${idx}`];
        if (!raw) return { strokes: null, thru: null };

        let thruVal: string | null = null;
        if (raw.thru === 'F') thruVal = '18';
        else if (raw.thru !== undefined && raw.thru !== null) thruVal = raw.thru;

        return { strokes: raw.strokes ?? null, thru: thruVal };
    }

    private extractTeeInfo(sc: any, totalRounds: number): [string | null, number | null] {
        for (let r = 1; r <= totalRounds; r++) {
            const rnd = sc[`round_${r}`];
            if (!rnd) continue;
            const tt = rnd.tee_time ?? null;
            const sh = rnd.starting_hole ?? null;
            if (tt !== null || sh !== null) return [tt, sh];
        }
        return [null, null];
    }

    private buildLeaderboard(): void {
        if (this.golfTour && this.golfTour()?.tournament_type === 'ryder_cup' && this.golfTour()?.events.length === 1) {
            this.isEmpty = true
            return
        }

        this.rows = [];
        this.headerRounds = [];
        this.winnerRow = null;
        this.golf_player = {} as GolfPlayer;
        // this.isEmpty = true;
        this.strokeEventStatus = null;

        if (!this.golfTour()?.events?.length) return;

        const event = this.golfTour()?.events.find(e => e.type === 'stroke_play');

        if (!event || !event.scores?.length) return;

        if (event.type === 'stroke_play' && (event.status === 'cancelled' || event.status === 'interrupted')) {
            this.strokeEventStatus = event.status;
        }

        const isFinished = event.status === 'finished';
        const totalRounds = event.number_of_rounds ?? 4;
        this.headerRounds = Array.from({ length: totalRounds }, (_, i) => i + 1);

        const currentRoundIdx = event.type === 'stroke_play'
            ? this.mostRecentRoundIndex(event.most_recently_scored_round)
            : null;

        event.scores.forEach((sc: any) => {
            const dq = !!sc.dq;
            const wd = !!sc.wd;
            const cut = !sc.made_cut && !dq && !wd;

            const rawPlace = sc.place;
            const placeNum: number | null =
                dq || wd || cut ? null :
                    rawPlace !== null && rawPlace !== undefined &&
                        rawPlace !== '' && !isNaN(Number(rawPlace))
                        ? Number(rawPlace)
                        : null;

            const pos: string = dq ? this.str(this.DQ_KEY) :
                wd ? this.str(this.WD_KEY) :
                    cut ? this.str(this.CUT_KEY) :
                        (sc.display_place ?? '-');

            const firstName = sc.player?.first_name ?? '';
            const lastName = sc.player?.last_name ?? '';

            const playerName = `${firstName.charAt(0)}. ${lastName}`;
            const fullName = `${firstName} ${lastName}`.trim();
            const photo = sc.player?.photo ?? null;

            /* total score (par) */
            const parVal: number | null | undefined = sc.par;
            let tot: string;
            if (parVal === null || parVal === undefined) tot = '-';
            else if (parVal === 0) tot = this.str(this.EVEN_PAR_KEY);
            else if (parVal > 0) tot = `+${parVal}`;
            else tot = String(parVal);

            const roundVals: (number | string)[] = [];
            const rounds: RoundScore[] = [];
            for (let r = 1; r <= totalRounds; r++) {
                const rs = this.getRound(sc, r);
                rounds.push(rs);
                roundVals.push(rs.strokes ?? '-');
            }

            let thru = '-';
            if (!(dq || wd || cut)) {
                for (let i = totalRounds - 1; i >= 0; i--) {
                    if (rounds[i].strokes !== null) {
                        const t = rounds[i].thru;
                        thru = t === '18' ? 'F' : (t ?? '-');
                        break;
                    }
                }
            }

            // If most_recently_scored_round's thru is null, show tee_time in user locale; else keep prior
            if (event.type === 'stroke_play' && !(dq || wd || cut) && currentRoundIdx) {
                const rd = sc[`round_${currentRoundIdx}`];
                const currentThru = rd?.thru ?? null;
                const tee = rd?.tee_time ?? null;
                if ((currentThru === null || currentThru === undefined) && tee) {
                    thru = formatLocalShortTimeFromCET(tee);
                }
            }

            const [teeTime, startingHole] = this.extractTeeInfo(sc, totalRounds);

            const wonViaPlayoff = isFinished && placeNum === 1 && this.didWinViaPlayoff(sc, totalRounds);

            this.rows.push({
                pos,
                placeNum,
                playerName,
                fullName,
                photo,
                player: sc.player,
                tot,
                thru,
                roundVals,
                teeTime,
                startingHole,
                pairId: sc.pair_id ?? null,
                sortOrder: (typeof sc.sort_order === 'number' ? sc.sort_order : null),
                wonViaPlayoff
            });
        });

        const isStrokePlay = event.type === 'stroke_play';

        // If server sends sort_order, use it for ordering.
        // Fallback to legacy sort logic only when sort_order is missing
        const canUseServerSort =
            isStrokePlay &&
            this.rows.some(r => typeof r.sortOrder === 'number');

        if (canUseServerSort) {
            this.rows.sort((a, b) => {
                const aSo = typeof a.sortOrder === 'number' ? a.sortOrder : Number.MAX_SAFE_INTEGER;
                const bSo = typeof b.sortOrder === 'number' ? b.sortOrder : Number.MAX_SAFE_INTEGER;

                const soCmp = aSo - bSo;
                if (soCmp !== 0) return soCmp;

                // tie-breakers (if sort_order ties)
                const pairCmp = this.comparePairId(a, b);
                if (pairCmp !== 0) return pairCmp;

                return a.playerName.localeCompare(b.playerName);
            });
        } else {
            this.rows.sort((a, b) => {
                const bucket = (r: LeaderRow): number => {
                    if (r.placeNum !== null) return 0;
                    if (r.pos.trim() && r.pos !== '-') return 1;
                    return 2;
                };

                const ba = bucket(a);
                const bb = bucket(b);
                if (ba !== bb) return ba - bb;



                switch (ba) {
                    case 0: {
                        const placeCmp = a.placeNum! - b.placeNum!;
                        if (placeCmp !== 0) return placeCmp;

                        // In a tie (same placeNum), subsort by pair_id
                        const pairCmp = this.comparePairId(a, b);
                        if (pairCmp !== 0) return pairCmp;

                        // fallback
                        return a.playerName.localeCompare(b.playerName);
                    }
                    case 1: {
                        const posCmp = a.pos.localeCompare(b.pos);
                        if (posCmp !== 0) return posCmp;

                        const pairCmp = this.comparePairId(a, b);
                        if (pairCmp !== 0) return pairCmp;

                        return a.playerName.localeCompare(b.playerName);
                    }
                    case 2: {
                        if (a.teeTime && b.teeTime) {
                            const tCmp = a.teeTime.localeCompare(b.teeTime);
                            if (tCmp !== 0) return tCmp;
                        } else if (a.teeTime || b.teeTime) {
                            return a.teeTime ? -1 : 1;
                        }
                        const sA = a.startingHole ?? Number.MAX_SAFE_INTEGER;
                        const sB = b.startingHole ?? Number.MAX_SAFE_INTEGER;
                        const holeCmp = sA - sB;
                        if (holeCmp !== 0) return holeCmp;

                        // fallback (pair_id if present)
                        const pairCmp = this.comparePairId(a, b);
                        if (pairCmp !== 0) return pairCmp;

                        return a.playerName.localeCompare(b.playerName);
                    }
                }
                return 0;
            });
        }

        const uniqueWinner = this.selectUniqueWinnerIfAny(isFinished, this.rows);
        this.winnerRow = uniqueWinner;
        this.golf_player = uniqueWinner?.player ?? ({} as GolfPlayer);

        this.isEmpty = this.rows.length === 0;
    }

    openPlayer(golf_player: GolfPlayer | Player): void {
        this.dialog.open(PlayerDialogComponent, {
            data: golf_player,
            width: '100%',
            height: '90vh',
            maxWidth: '750px',
            panelClass: 'full-screen-dialog'
        });
    }

    get isRyderCup(): boolean {
        return this.golfTour()?.tournament_type === 'ryder_cup';
    }

    get isMatchPlay(): boolean {
        return this.golfTour()?.tournament_type === 'match_play';
    }

    get statusLabel(): string {
        if (!this.strokeEventStatus) return '';
        const map: Record<'cancelled' | 'interrupted', string> = {
            cancelled: 'golf.eventstatus.cancelled.stroke',
            interrupted: 'golf.eventstatus.interrupted.stroke',
        };
        return this.str(map[this.strokeEventStatus]);
    }

    private mostRecentRoundIndex(roundKey?: string | null): number | null {
        if (!roundKey || roundKey === 'round_final') return null;
        const map: Record<string, number> = { round_1: 1, round_2: 2, round_3: 3, round_4: 4 };
        return map[roundKey] ?? null;
    }

    /**
     * Winner view should only appear if the tournament is finished AND there is exactly ONE player
     * with numeric place === 1.
     *
     * Why:
     * - Some events end with multiple "position 1" players (ties / shared scores).
     * - In that case we must NOT show the Winner View at all.
     */
    private selectUniqueWinnerIfAny(isFinished: boolean, rows: LeaderRow[]): LeaderRow | null {
        if (!isFinished) return null;

        // Count only numeric winners (placeNum === 1). display_place may be "T1" but place still equals 1.
        const winners = rows.filter((r) => r.placeNum === 1);

        // multiple position 1 players => hide winner view
        if (winners.length !== 1) return null;

        return winners[0];
    }

    // Helper to compare pair_id safely (handles undefined/null and numeric strings)
    private comparePairId(a: LeaderRow, b: LeaderRow): number {
        const aId = a.pairId;
        const bId = b.pairId;

        if (aId === null || aId === undefined) return (bId === null || bId === undefined) ? 0 : 1; // missing goes last
        if (bId === null || bId === undefined) return -1;

        const aNum = typeof aId === 'number' ? aId : (/^\d+$/.test(String(aId)) ? Number(aId) : null);
        const bNum = typeof bId === 'number' ? bId : (/^\d+$/.test(String(bId)) ? Number(bId) : null);

        if (aNum !== null && bNum !== null) return aNum - bNum;

        return String(aId).localeCompare(String(bId));
    }

    private didWinViaPlayoff(sc: any, totalRounds: number): boolean {
      // Find the last round that has strokes (last scored round)
      for (let r = totalRounds; r >= 1; r--) {
        const rnd = sc?.[`round_${r}`];
        if (!rnd) continue;

        // consider it "scored" if strokes exists
        const hasStrokes = rnd.strokes !== null && rnd.strokes !== undefined;
        if (!hasStrokes) continue;

        const extra = rnd.extra_scores;
        if (Array.isArray(extra)) return extra.length > 0;
        if (extra && typeof extra === 'object') return Object.keys(extra).length > 0;
        return !!extra;
      }
      return false;
    }

}
